/** Canonical SPA route paths (React Router). */

/** Prefix for authenticated platform console surfaces. */
export const DASHBOARD_BASE = "/dashboard";

export const ROUTES = {
  /** Public marketing landing (no force-redirect shell). */
  home: "/",
  /** Public developer integration docs (SPA + static /docs/.../index.md). */
  docs: "/docs",
  login: "/login",
  signup: "/signup",
  /** Console home (projects list). */
  dashboard: `${DASHBOARD_BASE}/projects`,
  hostedPlatformSignin: "/auth/sign-in",
  hostedPlatformSignup: "/auth/sign-up",
  /** MCP agent consent (D-0043). Login uses first-party /login, not portal standby. */
  hostedMCPAuthorize: "/auth/mcp/authorize",
  hostedProjectSignin: (projectSlug: string) => `/auth/${projectSlug}/sign-in`,
  hostedProjectSignup: (projectSlug: string) => `/auth/${projectSlug}/sign-up`,
  hostedProjectAuthorize: (projectSlug: string) =>
    `/auth/${projectSlug}/authorize`,
  projects: `${DASHBOARD_BASE}/projects`,
  operatorVerifications: `${DASHBOARD_BASE}/operator/application-verifications`,
  operatorVerification: (applicationId: string) =>
    `${DASHBOARD_BASE}/operator/application-verifications/${applicationId}`,
  project: (id: string) => `${DASHBOARD_BASE}/projects/${id}`,
  tables: (id: string) => `${DASHBOARD_BASE}/projects/${id}/tables`,
  table: (id: string, table: string) =>
    `${DASHBOARD_BASE}/projects/${id}/tables/${table}`,
  view: (id: string, view: string) =>
    `${DASHBOARD_BASE}/projects/${id}/views/${view}`,
  sql: (id: string) => `${DASHBOARD_BASE}/projects/${id}/sql`,
  database: (id: string) => `${DASHBOARD_BASE}/projects/${id}/database`,
  databaseSchemas: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/database/schemas`,
  databaseTables: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/database/tables`,
  databaseTable: (id: string, table: string) =>
    `${DASHBOARD_BASE}/projects/${id}/database/tables/${encodeURIComponent(table)}`,
  databaseFunctions: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/database/functions`,
  databaseTriggers: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/database/triggers`,
  permissions: (id: string) => `${DASHBOARD_BASE}/projects/${id}/permissions`,
  advisors: (id: string) => `${DASHBOARD_BASE}/projects/${id}/advisors`,
  advisorsSecurity: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/advisors/security`,
  applications: (id: string) => `${DASHBOARD_BASE}/projects/${id}/applications`,
  application: (id: string, applicationId: string) =>
    `${DASHBOARD_BASE}/projects/${id}/applications/${applicationId}`,
  /** @deprecated Prefer authUsers — kept for deep links. */
  users: (id: string) => `${DASHBOARD_BASE}/projects/${id}/users`,
  auth: (id: string) => `${DASHBOARD_BASE}/projects/${id}/auth`,
  authUsers: (id: string) => `${DASHBOARD_BASE}/projects/${id}/auth/users`,
  authSessions: (id: string) => `${DASHBOARD_BASE}/projects/${id}/auth/sessions`,
  authSchema: (id: string) => `${DASHBOARD_BASE}/projects/${id}/auth/schema`,
  usage: (id: string) => `${DASHBOARD_BASE}/projects/${id}/usage`,
  storage: (id: string) => `${DASHBOARD_BASE}/projects/${id}/storage`,
  storageImages: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/storage/images`,
  realtime: (id: string) => `${DASHBOARD_BASE}/projects/${id}/realtime`,
  functions: (id: string) => `${DASHBOARD_BASE}/projects/${id}/functions`,
  observability: (id: string) => `${DASHBOARD_BASE}/projects/${id}/observability`,
  observabilityDatabase: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/observability/database`,
  observabilityLogs: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/observability/logs`,
  observabilityAuth: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/observability/auth`,
  observabilityMCP: (id: string) =>
    `${DASHBOARD_BASE}/projects/${id}/observability/mcp`,
} as const;

/** Path patterns registered with React Router (for inventory / tests). */
export const ROUTE_PATTERNS = [
  "/",
  "/docs",
  "/docs/*",
  "/login",
  "/signup",
  "/auth/sign-in",
  "/auth/sign-up",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/mcp/authorize",
  "/auth/:projectSlug/sign-in",
  "/auth/:projectSlug/sign-up",
  "/auth/:projectSlug/forgot-password",
  "/auth/:projectSlug/reset-password",
  "/auth/:projectSlug/verify-email",
  "/auth/:projectSlug/authorize",
  `${DASHBOARD_BASE}/projects`,
  `${DASHBOARD_BASE}/operator/application-verifications`,
  `${DASHBOARD_BASE}/operator/application-verifications/:applicationId`,
  `${DASHBOARD_BASE}/projects/:id`,
  `${DASHBOARD_BASE}/projects/:id/tables`,
  `${DASHBOARD_BASE}/projects/:id/tables/:table`,
  `${DASHBOARD_BASE}/projects/:id/views/:view`,
  `${DASHBOARD_BASE}/projects/:id/sql`,
  `${DASHBOARD_BASE}/projects/:id/database`,
  `${DASHBOARD_BASE}/projects/:id/database/schemas`,
  `${DASHBOARD_BASE}/projects/:id/database/tables`,
  `${DASHBOARD_BASE}/projects/:id/database/tables/:table`,
  `${DASHBOARD_BASE}/projects/:id/database/functions`,
  `${DASHBOARD_BASE}/projects/:id/database/triggers`,
  `${DASHBOARD_BASE}/projects/:id/permissions`,
  `${DASHBOARD_BASE}/projects/:id/advisors`,
  `${DASHBOARD_BASE}/projects/:id/advisors/security`,
  `${DASHBOARD_BASE}/projects/:id/applications`,
  `${DASHBOARD_BASE}/projects/:id/applications/:applicationId`,
  `${DASHBOARD_BASE}/projects/:id/users`,
  `${DASHBOARD_BASE}/projects/:id/auth`,
  `${DASHBOARD_BASE}/projects/:id/auth/users`,
  `${DASHBOARD_BASE}/projects/:id/auth/sessions`,
  `${DASHBOARD_BASE}/projects/:id/auth/schema`,
  `${DASHBOARD_BASE}/projects/:id/usage`,
  `${DASHBOARD_BASE}/projects/:id/storage`,
  `${DASHBOARD_BASE}/projects/:id/storage/images`,
  `${DASHBOARD_BASE}/projects/:id/realtime`,
  `${DASHBOARD_BASE}/projects/:id/functions`,
  `${DASHBOARD_BASE}/projects/:id/observability`,
  `${DASHBOARD_BASE}/projects/:id/observability/database`,
  `${DASHBOARD_BASE}/projects/:id/observability/logs`,
  `${DASHBOARD_BASE}/projects/:id/observability/auth`,
  `${DASHBOARD_BASE}/projects/:id/observability/mcp`,
] as const;

/** Access class for SPA path classification (unit tests + guards). */
export type RouteAccess = "public" | "dashboard";

/**
 * Classify a pathname for guest vs platform-console access.
 * Public: landing, docs, login shortcuts, hosted auth portal.
 * Dashboard: `/dashboard/*` product console (and legacy `/projects`/`/operator` bookmarks).
 */
export function classifyPath(pathname: string): RouteAccess {
  const raw = (pathname.split("?")[0] ?? "/").trim() || "/";
  const p = raw.length > 1 && raw.endsWith("/") ? raw.slice(0, -1) : raw;

  if (p === "/" || p === "/docs" || p.startsWith("/docs/")) {
    return "public";
  }
  if (p === "/login" || p === "/signup" || p === "/account") {
    return "public";
  }
  if (p === "/auth" || p.startsWith("/auth/")) {
    return "public";
  }
  if (p === DASHBOARD_BASE || p.startsWith(`${DASHBOARD_BASE}/`)) {
    return "dashboard";
  }
  // Legacy unprefixed console paths (compat redirects).
  if (p === "/projects" || p.startsWith("/projects/")) {
    return "dashboard";
  }
  if (p === "/operator" || p.startsWith("/operator/")) {
    return "dashboard";
  }
  return "public";
}

/** True when the path is a platform console surface that expects a signed-in owner. */
export function requiresPlatformSession(pathname: string): boolean {
  return classifyPath(pathname) === "dashboard";
}

/**
 * Safe post-login continue target. Only allows same-origin relative paths
 * that are not open redirects.
 */
export function safeContinuePath(
  requested: string | null | undefined,
  fallback: string = ROUTES.projects,
): string {
  const raw = (requested ?? "").trim() || fallback;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) {
    return fallback;
  }
  return raw;
}

/** MCP consent URL for a pending `tb_mtx_` transaction. */
export function mcpAuthorizePath(transaction: string): string {
  return `${ROUTES.hostedMCPAuthorize}?transaction=${encodeURIComponent(transaction)}`;
}

/**
 * First-party Auth v2 login with a safe continue target.
 * Hosted Auth portal transaction login stays on standby (US-110 / BL-071).
 */
export function firstPartyLoginPath(continueTo: string): string {
  return `${ROUTES.login}?continue=${encodeURIComponent(safeContinuePath(continueTo))}`;
}

/** Map legacy console paths to `/dashboard` equivalents when needed. */
export function legacyToDashboardPath(pathname: string): string | null {
  const p = (pathname.split("?")[0] ?? "").trim() || "/";
  if (p === "/projects" || p.startsWith("/projects/")) {
    return `${DASHBOARD_BASE}${p}`;
  }
  if (p === "/operator" || p.startsWith("/operator/")) {
    return `${DASHBOARD_BASE}${p}`;
  }
  return null;
}
